import { resolveMentions } from '@trevize/api/modules/implementations/session-mentions'
import type { RunMessageMention } from '@trevize/db'

import type { RunMessage } from '../../zero-schema.gen.ts'

/** The `run_messages` columns the session composer writes optimistically. */
export type OptimisticSessionMessage = Pick<
  RunMessage,
  | 'id'
  | 'workspaceId'
  | 'runId'
  | 'role'
  | 'userId'
  | 'parts'
  | 'mentions'
  | 'metadata'
  | 'status'
  | 'turnId'
  | 'createdAt'
>

export interface OptimisticSessionMessageInput {
  id: string
  workspaceId: string
  runId: string
  /** The sending user — always present, since only members can post. */
  userId: string
  text: string
  /** `@user` mentions the composer resolved against the roster. */
  mentions?: RunMessageMention[]
  /** Send time; the row is ordered by this, so it can't be left unset. */
  createdAt: number
}

/**
 * Build the `run_messages` row the Zero client mutator inserts so a sent
 * message renders immediately, before the server round-trip.
 *
 * This deliberately mirrors what `postSessionMessage` persists server-side —
 * same id, mentions, parts and role — so when the authoritative row syncs back
 * it replaces this one silently instead of the message visibly changing or
 * duplicating.
 */
export function buildOptimisticSessionMessage(
  input: OptimisticSessionMessageInput,
): OptimisticSessionMessage {
  const mentions = resolveMentions(input.text, input.mentions)

  return {
    id: input.id,
    workspaceId: input.workspaceId,
    runId: input.runId,
    role: 'user',
    userId: input.userId,
    parts: [{ type: 'text', text: input.text, state: 'done' }],
    mentions,
    metadata: { userId: input.userId, mentions },
    status: 'complete',
    // Not yet picked up by a turn — this is what makes it count as "queued".
    turnId: null,
    createdAt: input.createdAt,
  }
}
